/**
 * TimezonePicker - Reusable timezone selector with autocomplete
 * Defaults to user's configured timezone from preferences.
 * Uses Command/Combobox pattern for searchable timezone selection.
 */

import React, { useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe, Check, ChevronsUpDown } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../../components/ui/command';
import { useUserPreferences } from '../context';
import { getBrowserTimezone, getUtcOffset } from '../utils/timezone';

export interface TimezonePickerProps {
  /** IANA timezone string (e.g., "America/New_York") */
  value: string | null;
  onChange: (value: string) => void;
  label?: string;
  disabled?: boolean;
  required?: boolean;
  fullWidth?: boolean;
  size?: 'sm' | 'default';
  className?: string;
  id?: string;
  /** If true, auto-fills with browser timezone when value is empty */
  autoDefault?: boolean;
}

interface TimezoneOption {
  value: string;
  label: string;
  region: string;
  offset: string;
  /** Extra searchable terms: country names, alternative city names */
  keywords: string;
}

interface TimezoneEntry {
  value: string;
  region: string;
  city: string;
  /** Country names, alternative cities, common names for search */
  keywords?: string;
}

/** Common IANA timezones grouped by region, with search keywords */
const TIMEZONE_DATA: TimezoneEntry[] = [
  // UTC
  { value: 'UTC', region: 'UTC', city: 'UTC', keywords: 'Coordinated Universal Time Greenwich' },
  // Americas
  { value: 'America/New_York', region: 'Americas', city: 'New York (Eastern)', keywords: 'USA United States EST EDT Washington DC Miami Boston Philadelphia Atlanta' },
  { value: 'America/Chicago', region: 'Americas', city: 'Chicago (Central)', keywords: 'USA United States CST CDT Houston Dallas Austin San Antonio' },
  { value: 'America/Denver', region: 'Americas', city: 'Denver (Mountain)', keywords: 'USA United States MST MDT Salt Lake City Phoenix' },
  { value: 'America/Los_Angeles', region: 'Americas', city: 'Los Angeles (Pacific)', keywords: 'USA United States PST PDT San Francisco Seattle Portland Silicon Valley' },
  { value: 'America/Anchorage', region: 'Americas', city: 'Anchorage (Alaska)', keywords: 'USA United States AKST AKDT' },
  { value: 'Pacific/Honolulu', region: 'Americas', city: 'Honolulu (Hawaii)', keywords: 'USA United States HST' },
  { value: 'America/Toronto', region: 'Americas', city: 'Toronto', keywords: 'Canada EST EDT Ottawa Montreal' },
  { value: 'America/Vancouver', region: 'Americas', city: 'Vancouver', keywords: 'Canada PST PDT' },
  { value: 'America/Mexico_City', region: 'Americas', city: 'Mexico City', keywords: 'Mexico CST CDT Guadalajara Monterrey' },
  { value: 'America/Bogota', region: 'Americas', city: 'Bogota', keywords: 'Colombia COT' },
  { value: 'America/Sao_Paulo', region: 'Americas', city: 'Sao Paulo', keywords: 'Brazil BRT Rio de Janeiro' },
  { value: 'America/Argentina/Buenos_Aires', region: 'Americas', city: 'Buenos Aires', keywords: 'Argentina ART' },
  // Europe
  { value: 'Europe/London', region: 'Europe', city: 'London (GMT)', keywords: 'UK United Kingdom England Britain BST Manchester Birmingham' },
  { value: 'Europe/Paris', region: 'Europe', city: 'Paris (CET)', keywords: 'France CEST Lyon Marseille' },
  { value: 'Europe/Berlin', region: 'Europe', city: 'Berlin (CET)', keywords: 'Germany Deutschland CEST Munich Frankfurt Hamburg' },
  { value: 'Europe/Madrid', region: 'Europe', city: 'Madrid (CET)', keywords: 'Spain España CEST Barcelona' },
  { value: 'Europe/Rome', region: 'Europe', city: 'Rome (CET)', keywords: 'Italy Italia CEST Milan' },
  { value: 'Europe/Amsterdam', region: 'Europe', city: 'Amsterdam (CET)', keywords: 'Netherlands Holland CEST Rotterdam' },
  { value: 'Europe/Zurich', region: 'Europe', city: 'Zurich (CET)', keywords: 'Switzerland CEST Geneva Basel' },
  { value: 'Europe/Stockholm', region: 'Europe', city: 'Stockholm (CET)', keywords: 'Sweden CEST' },
  { value: 'Europe/Helsinki', region: 'Europe', city: 'Helsinki (EET)', keywords: 'Finland EEST' },
  { value: 'Europe/Athens', region: 'Europe', city: 'Athens (EET)', keywords: 'Greece EEST' },
  { value: 'Europe/Bucharest', region: 'Europe', city: 'Bucharest (EET)', keywords: 'Romania EEST' },
  { value: 'Europe/Moscow', region: 'Europe', city: 'Moscow', keywords: 'Russia MSK Saint Petersburg' },
  { value: 'Europe/Istanbul', region: 'Europe', city: 'Istanbul', keywords: 'Turkey Türkiye TRT Ankara' },
  // Middle East & Africa
  { value: 'Asia/Jerusalem', region: 'Middle East & Africa', city: 'Jerusalem (IST)', keywords: 'Israel Tel Aviv Haifa IDT' },
  { value: 'Asia/Dubai', region: 'Middle East & Africa', city: 'Dubai (GST)', keywords: 'UAE United Arab Emirates Abu Dhabi' },
  { value: 'Asia/Riyadh', region: 'Middle East & Africa', city: 'Riyadh', keywords: 'Saudi Arabia AST Jeddah' },
  { value: 'Africa/Cairo', region: 'Middle East & Africa', city: 'Cairo (EET)', keywords: 'Egypt' },
  { value: 'Africa/Johannesburg', region: 'Middle East & Africa', city: 'Johannesburg (SAST)', keywords: 'South Africa Cape Town' },
  { value: 'Africa/Lagos', region: 'Middle East & Africa', city: 'Lagos (WAT)', keywords: 'Nigeria West Africa' },
  // Asia & Pacific
  { value: 'Asia/Kolkata', region: 'Asia & Pacific', city: 'Kolkata (IST)', keywords: 'India Mumbai Delhi Bangalore Chennai Hyderabad' },
  { value: 'Asia/Shanghai', region: 'Asia & Pacific', city: 'Shanghai (CST)', keywords: 'China Beijing Shenzhen Guangzhou' },
  { value: 'Asia/Hong_Kong', region: 'Asia & Pacific', city: 'Hong Kong (HKT)', keywords: 'China HK' },
  { value: 'Asia/Singapore', region: 'Asia & Pacific', city: 'Singapore (SGT)', keywords: 'SG' },
  { value: 'Asia/Tokyo', region: 'Asia & Pacific', city: 'Tokyo (JST)', keywords: 'Japan Osaka' },
  { value: 'Asia/Seoul', region: 'Asia & Pacific', city: 'Seoul (KST)', keywords: 'South Korea Busan' },
  { value: 'Asia/Bangkok', region: 'Asia & Pacific', city: 'Bangkok (ICT)', keywords: 'Thailand' },
  // Oceania
  { value: 'Australia/Sydney', region: 'Oceania', city: 'Sydney (AEST)', keywords: 'Australia AEDT' },
  { value: 'Australia/Melbourne', region: 'Oceania', city: 'Melbourne (AEST)', keywords: 'Australia AEDT' },
  { value: 'Australia/Perth', region: 'Oceania', city: 'Perth (AWST)', keywords: 'Australia' },
  { value: 'Pacific/Auckland', region: 'Oceania', city: 'Auckland (NZST)', keywords: 'New Zealand NZDT Wellington' },
];

function buildOptions(): TimezoneOption[] {
  return TIMEZONE_DATA.map((tz) => ({
    value: tz.value,
    label: tz.city,
    region: tz.region,
    offset: getUtcOffset(tz.value),
    keywords: tz.keywords || '',
  }));
}

function buildGrouped(options: TimezoneOption[]): Map<string, TimezoneOption[]> {
  const map = new Map<string, TimezoneOption[]>();
  for (const opt of options) {
    const existing = map.get(opt.region) || [];
    existing.push(opt);
    map.set(opt.region, existing);
  }
  return map;
}

/**
 * Custom filter for timezone search that prioritizes word-prefix matches
 * over cmdk's default fuzzy character-by-character matching.
 * Returns 1 for word-prefix matches (highest), 0.01 for substring matches,
 * and 0 for no match — effectively suppressing fuzzy noise like
 * "israel" matching "Los Angeles" via scattered characters.
 */
function timezoneFilter(value: string, search: string): number {
  const normalizedValue = value.toLowerCase();
  const searchTerms = search.toLowerCase().trim().split(/\s+/);

  // Check if every search term matches the start of any word in the value
  const words = normalizedValue.split(/[\s/+\-_()]+/).filter(Boolean);
  const allPrefixMatch = searchTerms.every((term) =>
    words.some((w) => w.startsWith(term))
  );
  if (allPrefixMatch) return 1;

  // Fallback: substring match (e.g. "york" matching "New York")
  const allSubstringMatch = searchTerms.every((term) =>
    normalizedValue.includes(term)
  );
  if (allSubstringMatch) return 0.5;

  return 0;
}

export function TimezonePicker({
  value,
  onChange,
  label,
  disabled,
  required,
  fullWidth = true,
  size = 'default',
  className,
  id,
  autoDefault = true,
}: TimezonePickerProps) {
  const { t } = useTranslation('common');
  const { formatOptions } = useUserPreferences();
  const generatedId = React.useId();
  const inputId = id || `tz-picker-${generatedId}`;
  const [open, setOpen] = useState(false);

  // Build flat options list and grouped map
  const allOptions = useMemo(() => buildOptions(), []);
  const grouped = useMemo(() => buildGrouped(allOptions), [allOptions]);

  // Use browser timezone as default if autoDefault and no value
  const effectiveValue = value || (autoDefault ? (formatOptions.timezone || getBrowserTimezone()) : '');

  // Check if user's timezone is in the predefined list; if not, create an entry for it
  const userTzOption = useMemo(() => {
    const userTz = formatOptions.timezone;
    if (!userTz || allOptions.some((o) => o.value === userTz)) return null;
    return {
      value: userTz,
      label: userTz,
      region: t('app.yourTimezone'),
      offset: getUtcOffset(userTz),
    };
  }, [formatOptions.timezone, allOptions, t]);

  // Find the display label for the currently selected value
  const selectedLabel = useMemo(() => {
    if (!effectiveValue) return '';
    const found = allOptions.find((o) => o.value === effectiveValue);
    if (found) return `${found.label} (${found.offset})`;
    if (userTzOption && userTzOption.value === effectiveValue) {
      return `${userTzOption.label} (${userTzOption.offset})`;
    }
    // Fallback: show raw value with offset
    const offset = getUtcOffset(effectiveValue);
    return offset ? `${effectiveValue} (${offset})` : effectiveValue;
  }, [effectiveValue, allOptions, userTzOption]);

  const handleSelect = useCallback(
    (tzValue: string) => {
      onChange(tzValue);
      setOpen(false);
    },
    [onChange],
  );

  return (
    <div className={cn(fullWidth && 'w-full', className)}>
      {label && (
        <div className="mb-1.5 leading-none">
          <Label htmlFor={inputId} required={required}>
            <Globe className="inline h-3.5 w-3.5 me-1 text-muted-foreground" />
            {label}
          </Label>
        </div>
      )}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            id={inputId}
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled}
            className={cn(
              'w-full justify-between font-normal',
              size === 'sm' && 'h-8 text-sm',
              !effectiveValue && 'text-muted-foreground',
            )}
          >
            <span className="truncate">
              {selectedLabel || t('app.selectTimezone')}
            </span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
          <Command filter={timezoneFilter}>
            <CommandInput placeholder={t('app.searchTimezone')} />
            <CommandList>
              <CommandEmpty>{t('app.noTimezoneFound')}</CommandEmpty>
              {/* User's custom timezone if not in the predefined list */}
              {userTzOption && (
                <CommandGroup heading={userTzOption.region}>
                  <CommandItem
                    value={`${userTzOption.label} ${userTzOption.value}`}
                    onSelect={() => handleSelect(userTzOption.value)}
                  >
                    <Check
                      className={cn(
                        'mr-2 h-4 w-4',
                        effectiveValue === userTzOption.value ? 'opacity-100' : 'opacity-0',
                      )}
                    />
                    <span className="flex items-center justify-between gap-2 flex-1">
                      <span>{userTzOption.label}</span>
                      <span className="text-xs text-muted-foreground">{userTzOption.offset}</span>
                    </span>
                  </CommandItem>
                </CommandGroup>
              )}
              {Array.from(grouped.entries()).map(([region, tzs]) => (
                <CommandGroup key={region} heading={region}>
                  {tzs.map((tz) => (
                    <CommandItem
                      key={tz.value}
                      value={`${tz.label} ${tz.value} ${tz.region} ${tz.keywords}`}
                      onSelect={() => handleSelect(tz.value)}
                    >
                      <Check
                        className={cn(
                          'mr-2 h-4 w-4',
                          effectiveValue === tz.value ? 'opacity-100' : 'opacity-0',
                        )}
                      />
                      <span className="flex items-center justify-between gap-2 flex-1">
                        <span>{tz.label}</span>
                        <span className="text-xs text-muted-foreground">{tz.offset}</span>
                      </span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              ))}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
